import { useCallback, useMemo } from 'react';

import type {
    GetPayloadArg,
    HookOptions,
    QueryAllPayloadCreatorType,
    UseEntities,
    UseEntitiesState,
} from './createEntityTypes';
import type { UsePaginatedEntities } from '../paginationEntity/createPaginationEntityTypes';
import type { AgnosticCondition, CacheKey } from '../utils/internalTypings';
import { mapListToIndexedObject } from './createEntityUtils';
import { useDynamicEnabled } from '../utils/useDynamicEnabled';

const READONLY_EMPTY_ARRAY = Object.freeze([]) as readonly unknown[];

export const buildUseEntities = <
    QueryAllPayloadCreator extends QueryAllPayloadCreatorType<Entity, any>,
    Entity,
>(
    selectId: CacheKey<Entity>,
    useEntities: UsePaginatedEntities<Entity, any, unknown>,
    condition: AgnosticCondition<GetPayloadArg<QueryAllPayloadCreator>> | undefined,
    invalidateList: () => void,
): UseEntities<Entity, GetPayloadArg<QueryAllPayloadCreator>> => {
    return (
        args?: GetPayloadArg<QueryAllPayloadCreator>,
        opts?: HookOptions,
    ): UseEntitiesState<Entity> => {
        const enabled = useDynamicEnabled({
            args,
            condition,
            fetchOnMount: opts?.fetchOnMount,
        });

        const {
            isPending,
            data = READONLY_EMPTY_ARRAY as Entity[],
            refetch,
            error,
            didInvalidate,
            lastUpdated,
        } = useEntities(args as GetPayloadArg<QueryAllPayloadCreator, any>, {
            enabled,
        });

        const entities: Record<string, Entity> = useMemo(
            () => mapListToIndexedObject(data, selectId),
            [data],
        );
        const selectEntityById = useCallback(
            (id: string) => {
                return entities[id];
            },
            [entities],
        );
        const entitiesIds = useMemo(() => Object.keys(entities), [entities]);

        return {
            entitiesCount: data.length,
            entities,
            refetch,
            isPending,
            error,
            allEntities: data,
            entitiesIds,
            selectEntityById,
            invalidate: invalidateList,
            didInvalidate,
            lastUpdated,
        };
    };
};
